import { useChat as useAiChat } from '@ai-sdk/react';
import { type ChatStatus, DefaultChatTransport, type TextUIPart, type UIMessage } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ENDPOINTS } from '@/api/endpoints';
import type {
  GetAssistantSessionsBySessionIdMessages200MessagesItemCitationsAnyOf as CitationMap,
  PostAssistantChatBody,
  PostAssistantChatBodyContext,
} from '@/api/generated/lucioAPI.schemas';
import { endSessionIfSignedInElsewhere } from '@/api/mutator';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { getAuthHeaders } from '@/config/auth/deviceToken';
import { getComposeBody, isInComposeMode, isReadingDraft, saveDraft } from '@/config/office/office';
import { apiBaseUrl } from '@/utils/apiBaseUrl';
import { getAssistantSessionRoute } from '@/utils/routeUtils';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';

export type MessageMetadata = {
  assistantMessageId?: string;
  citations?: CitationMap;
  contextEmailIds?: Array<string>;
  sessionId?: string;
};

export type MessageDataPart = {
  citations?: CitationMap;
  content?: string;
  contentType?: 'text' | 'email_draft' | 'revision';
};

export type AssistantUIMessage = UIMessage;

// Keep in sync with backend MUTATING_TOOL_NAMES
// (apps/backend/src/features/actions/constants.ts).
const MUTATING_TOOL_NAMES: ReadonlySet<string> = new Set([
  'createAndSaveDraft',
  'createCategory',
  'createFolder',
  'moveEmailToFolder',
  'renameFolder',
  'sendEmail',
  'sendWebhook',
]);

export function getMessageText(msg: AssistantUIMessage): string {
  return msg.parts
    .filter((p): p is TextUIPart => p.type === 'text')
    .map((p) => p.text)
    .join('');
}

export function getMessageMeta(msg: AssistantUIMessage): MessageMetadata {
  return (msg.metadata as MessageMetadata | undefined) ?? {};
}

export function getMessageDataPart(msg: AssistantUIMessage): MessageDataPart | undefined {
  const part = msg.parts.find((p) => p.type === 'data-message-meta');
  if (!part) {
    return undefined;
  }
  return (part as { data: MessageDataPart; type: string }).data;
}

/** The chat stream's fetch: a 401 `signed_in_elsewhere` ends the session as any request does. */
async function chatFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  await endSessionIfSignedInElsewhere(response);
  return response;
}

interface UseChatOptions {
  urlSessionId: string | undefined;
}

interface UseChatResult {
  clearMessages: () => void;
  getMessageDataPart: typeof getMessageDataPart;
  getMessageMeta: typeof getMessageMeta;
  getMessageText: typeof getMessageText;
  handleSend: (message: string, attachmentIds: Array<string>) => Promise<void>;
  isLoading: boolean;
  messages: Array<AssistantUIMessage>;
  status: ChatStatus;
  stop: () => void;
}

export function useChat({ urlSessionId }: UseChatOptions): UseChatResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const urlSessionIdRef = useRef(urlSessionId);
  urlSessionIdRef.current = urlSessionId;

  const trackedActionToolCallIdsRef = useRef<Set<string>>(new Set());

  const transportRef = useRef<DefaultChatTransport<AssistantUIMessage> | null>(null);
  if (!transportRef.current) {
    transportRef.current = new DefaultChatTransport<AssistantUIMessage>({
      api: `${apiBaseUrl()}${ENDPOINTS.ASSISTANT.SEND_MESSAGE}`,
      fetch: chatFetch,
      headers: async () => (await getAuthHeaders()) ?? {},
      prepareSendMessagesRequest: async ({ body, messages }) => {
        const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
        const prompt =
          lastUserMsg?.parts
            .filter((p): p is TextUIPart => p.type === 'text')
            .map((p) => p.text)
            .join('') ?? '';

        return {
          body: {
            prompt,
            ...(body as object),
          },
        };
      },
    });
  }

  const { messages, sendMessage, setMessages, status, stop } = useAiChat<AssistantUIMessage>({
    onData: (part) => {
      if (part.type === 'data-skipped-attachments') {
        const data = (
          part as {
            data?: {
              skippedAttachments?: Array<{
                fileName: string;
                reason?: 'extraction_failed' | 'file_too_large' | 'link_only' | 'token_limit';
              }>;
            };
          }
        ).data;
        const skipped = data?.skippedAttachments ?? [];
        if (skipped.length === 0) {
          return;
        }
        const descriptions = skipped
          .map((s) => {
            const reason = s.reason ? t(`assistant.chat.attachmentSkipReason.${s.reason}`) : '';
            return reason ? `${s.fileName} — ${reason}` : s.fileName;
          })
          .filter(Boolean)
          .join('\n');
        toast.warning(t('assistant.chat.attachmentsSkipped', { count: skipped.length }), {
          description: descriptions,
        });
      }
    },
    onError: (err) => {
      toast.error(t('assistant.chat.streamInterrupted'), { description: err.message });
    },
    transport: transportRef.current,
  });

  // Navigate to session URL when the first assistant message arrives with a session ID
  useEffect(() => {
    if (urlSessionIdRef.current) {
      return;
    }
    const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistantMsg) {
      return;
    }
    const meta = getMessageMeta(lastAssistantMsg);
    if (meta.sessionId) {
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_SESSION_CREATED, {
        sessionId: meta.sessionId,
      });
      navigate(getAssistantSessionRoute(meta.sessionId), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, navigate]);

  useEffect(() => {
    for (const msg of messages) {
      if (msg.role !== 'assistant') {
        continue;
      }
      for (const part of msg.parts) {
        const p = part as {
          output?: { success?: unknown };
          state?: string;
          toolCallId?: string;
          toolName?: string;
          type?: string;
        };
        if (p.type !== 'dynamic-tool' || p.state !== 'output-available') {
          continue;
        }
        const toolCallId = p.toolCallId;
        const toolName = p.toolName;
        if (!toolCallId || !toolName || !MUTATING_TOOL_NAMES.has(toolName)) {
          continue;
        }
        if (trackedActionToolCallIdsRef.current.has(toolCallId)) {
          continue;
        }
        trackedActionToolCallIdsRef.current.add(toolCallId);
        posthog.capture(ANALYTICS_EVENTS.ASSISTANT_ACTION_TAKEN, {
          sessionId: getMessageMeta(msg).sessionId ?? urlSessionIdRef.current,
          success: p.output?.success === true,
          toolName,
        });
      }
    }
  }, [messages, posthog]);

  const buildContextBody = useCallback(
    async (attachmentIds: Array<string>): Promise<Omit<PostAssistantChatBody, 'prompt'>> => {
      const state = useOfficeContextStore.getState();
      const filterMode = state.getCurrentFilterMode();
      const contextInfo = state.contextInfo;
      const selectedCustomEmails = state.selectedCustomEmails;
      const selectedFolderId = state.selectedFolderId;
      const selectedFolderName = state.selectedFolderName;
      const activeSessionContext = state.activeSessionContext;

      let composeBody: string | undefined;
      if (isInComposeMode() || isReadingDraft()) {
        try {
          await saveDraft();
          composeBody = await getComposeBody();
        } catch {
          /* ignore */
        }
      }

      const base = {
        ...(attachmentIds.length > 0 && { attachments: attachmentIds }),
        ...(composeBody && { composeBody }),
        ...(urlSessionIdRef.current && { sessionId: urlSessionIdRef.current }),
      };

      switch (filterMode) {
        case 'currentEmailThread':
          if (activeSessionContext?.context === 'EMAIL' || !contextInfo.conversationId) {
            return {
              ...base,
              context: 'EMAIL' as PostAssistantChatBodyContext,
              ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
              ...(contextInfo.conversationId && { currentThreadId: contextInfo.conversationId }),
            };
          }
          return {
            ...base,
            context: 'THREAD' as PostAssistantChatBodyContext,
            currentThreadId: contextInfo.conversationId,
            ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
          };
        case 'custom':
          return {
            ...base,
            context: 'CUSTOM' as PostAssistantChatBodyContext,
            ...(selectedCustomEmails.length > 0 && { customEmailIds: selectedCustomEmails }),
            ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
            ...(contextInfo.conversationId && { currentThreadId: contextInfo.conversationId }),
          };
        case 'folder':
          // No folder picked yet — degrade to INBOX so the agent has something to work with.
          if (!selectedFolderId) {
            return {
              ...base,
              context: 'INBOX' as PostAssistantChatBodyContext,
              ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
              ...(contextInfo.conversationId && { currentThreadId: contextInfo.conversationId }),
            };
          }
          return {
            ...base,
            context: 'FOLDER' as PostAssistantChatBodyContext,
            folderId: selectedFolderId,
            ...(selectedFolderName && { folderName: selectedFolderName }),
            ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
            ...(contextInfo.conversationId && { currentThreadId: contextInfo.conversationId }),
          };
        default:
          return {
            ...base,
            context: 'INBOX' as PostAssistantChatBodyContext,
            ...(contextInfo.conversationId && { currentThreadId: contextInfo.conversationId }),
            ...(contextInfo.itemId && { currentEmailId: contextInfo.itemId }),
          };
      }
    },
    [],
  );

  const handleSend = useCallback(
    async (message: string, attachmentIds: Array<string>) => {
      const contextBody = await buildContextBody(attachmentIds);
      const officeState = useOfficeContextStore.getState();
      const filterMode = officeState.getCurrentFilterMode();
      posthog.capture(ANALYTICS_EVENTS.ASSISTANT_MESSAGE_SENT, {
        filterMode,
        fromHistory: !!urlSessionIdRef.current,
        hasAttachments: attachmentIds.length > 0,
        selectedEmailCount: filterMode === 'custom' ? officeState.selectedCustomEmails.length : 0,
      });
      await sendMessage({ text: message }, { body: contextBody });
    },
    [buildContextBody, posthog, sendMessage],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, [setMessages]);

  return {
    clearMessages,
    getMessageDataPart,
    getMessageMeta,
    getMessageText,
    handleSend,
    isLoading: status === 'submitted' || status === 'streaming',
    messages,
    status,
    stop,
  };
}
